import { TerrainType } from "@/constant.js";
import { i18n } from "@/i18n.js";
import Design from "@/models/Design";
import Line from "@/models/Line";
import PointCloud from "@/models/PointCloud";
import { type IObject, ObjectType } from "@/models/objects/types.js";
import store from "@/store/index";
import { notifyError } from "@/utils/notificationUtils";
import axios from "axios";
import { md5 } from "hash-wasm";
import { v4 as uuidv4 } from "uuid";
import { getCookie } from "./getCookie.js";
import localCacheDB from "./local-db.js";
import { convertPointsToPointCloudCRS } from "./wsConvertToPointCloudCRS.js";
import { convertPointsToWGS84CRS } from "./wsConvertToWGS84.js";

const worker = new Worker(
	new URL("./DataConversionWorker.js", import.meta.url),
	{ type: "module" },
);

function debounce<T extends (...args: any[]) => any>(func: T, timeout = 300) {
	let timer: ReturnType<typeof setTimeout>;
	return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
		return new Promise<ReturnType<T>>((resolve, reject) => {
			clearTimeout(timer);
			timer = setTimeout(() => {
				try {
					const result = func.apply(this, args);
					if (result instanceof Promise) {
						result.then(resolve).catch(reject);
					} else {
						resolve(result);
					}
				} catch (error) {
					reject(error);
				}
			}, timeout);
		});
	};
}

export const startWorker = () => {
	const wsServerUrl =
		import.meta.env.VITE_WEB_SOCKET_URL + "/wsPointCloudTransformer";
	const data = { action: "INIT", payload: wsServerUrl };
	worker.postMessage(JSON.stringify({ data }));
};

export const uploadRoadObj = debounce((props) => {
	const objConnectionJSON = store.getters.viewer.getConnectionsInSiteJSON();
	return store.dispatch("object/updateObj", {
		props,
		objConnectionJSON,
	});
}, 300);

export const uploadTrenchObj = debounce((props) => {
	return store.dispatch("object/updateObj", { props });
}, 300);

export const uploadFlatGroundObj = debounce((props) => {
	const objConnectionJSON = store.getters.viewer.getConnectionsInSiteJSON();
	return store.dispatch("object/updateObj", {
		props,
		objConnectionJSON,
	});
}, 300);

export const uploadSolidMeshObj = debounce((props) => {
	return store.dispatch("object/updateObj", {
		props,
	});
}, 300);

export const getJsonOfDrawing2dImage = (imageId) => {
	const json = store.getters.viewer.getJSONOfDrawing2dImage(imageId);
	return json;
};

export const uploadDrawing2dImage = debounce(async (imageId, siteId) => {
	const json = store.getters.viewer.getJSONOfDrawing2dImage(imageId);
	const response = await axios.put(
		`${import.meta.env.VITE_API_BASE}/drawing2dImage/setting/${siteId}/${imageId}`,
		{
			json,
		},
		{
			auth: store.state.authObject,
		},
	);
}, 300);

/**
 * オブジェクトの更新処理に共通のエラーメッセージ出力を追加したラッパーメソッド
 * @param {*} props
 * @param {*} objType 処理対象のオブジェクトのタイプ
 */
export const uploadObjAndNotify = async (props, objType) => {
	try {
		if (objType === ObjectType.ROAD) {
			await uploadRoadObj(props);
		} else if (objType === ObjectType.FLAT_GROUND) {
			await uploadFlatGroundObj(props);
		} else if (objType === ObjectType.TRENCH) {
			await uploadTrenchObj(props);
		} else if (objType === ObjectType.SOLID_MESH) {
			await uploadSolidMeshObj(props);
		}
	} catch (e) {
		const status = e.response?.status;
		if (status === 409) {
			notifyError(
				{ $store: store, $t: i18n.global.t },
				i18n.global.t("EDITING_BY_OTHER_USER"),
			);
		} else {
			notifyError(
				{ $store: store, $t: i18n.global.t },
				`${i18n.global.t("UPDATE_DATA")} ${i18n.global.t("failed")}`,
			);
		}
	} finally {
		if (store.state.isObjectUpdating) {
			store.dispatch("endObjectUpdating");
		}
	}
};

/**
 * 基準地形の可視不可視を変更する
 * @param {*} pointCloud
 */
export const togglePointCloudVisibility = async (pointCloud) => {
	if (!pointCloud.getVisibility()) {
		// 不可視から可視への切り替え
		if (pointCloud.getIsTiling()) {
			// タイリング済みの場合、新たにタイリング処理を行わない
			pointCloud.setVisibility(true);
			store.getters.viewer.updateTilesetVisibility(
				Number.parseInt(pointCloud.getAssetId()),
				"pointcloud",
				pointCloud.getVisibility(),
			);
			// 表示した設計データにフォーカスする
			zoomToTileset(pointCloud);
			await localCacheDB.addPointCloud(
				store.state.user.id,
				store.state.site.site_uuid,
				pointCloud.getAssetId(),
			);
		} else {
			// スピナーを表示する
			store.dispatch("updateIsLoading", true);
			// タイリング中のステータスをtrueにする
			pointCloud.setTilingProgress(true);

			// タイリングのためのデータを取得し、タイリング処理を実行する
			try {
				await addPointCloudTileset(pointCloud);

				// ステータスを可視状態かつタイリング済みにする
				pointCloud.setVisibility(true);
				pointCloud.setIsTiling(true);

				// 表示点群リストに追加
				// store.dispatch("addToVisiblePointCloudAndDesignList", pointCloud);

				// タイリング中のステータスをfalseに戻す
				pointCloud.setTilingProgress(false);

				// スピナーを非表示にする
				store.dispatch("updateIsLoading", false);
				await localCacheDB.addPointCloud(
					store.state.user.id,
					store.state.site.site_uuid,
					pointCloud.getAssetId(),
				);
			} catch (err) {
				const msg = err?.message ?? String(err);
				console.error(msg);
				// タイリング中のステータスをfalseに戻す
				pointCloud.setTilingProgress(false);
				// スピナーを非表示にする
				store.dispatch("updateIsLoading", false);
				await localCacheDB.deletePointCloud(
					store.state.user.id,
					store.state.site.site_uuid,
					pointCloud.getAssetId(),
				);

				if (msg === "conversion error") {
					notifyError(
						{ $store: store, $t: i18n.global.t },
						`座標系が異なるため、タイリング中で計算で失敗`,
					);
				}
				throw err;
			}
		}
	} else {
		// 可視から不可視への切り替え
		pointCloud.setVisibility(false);
		await localCacheDB.deletePointCloud(
			store.state.user.id,
			store.state.site.site_uuid,
			pointCloud.getAssetId(),
		);
		store.getters.viewer.updateTilesetVisibility(
			Number.parseInt(pointCloud.getAssetId()),
			"pointcloud",
			pointCloud.getVisibility(),
		);
		// 表示点群リストから削除
		// store.dispatch("removeFromVisiblePointCloudAndDesignList", pointCloud);
		// 点群に表示されている線形、距離、断面を削除する
		store.getters["measurement/measurementList"]
			.filter((item) => item.assetId == pointCloud.getAssetId())
			.forEach((item) => {
				deleteMeasurement(item);
			});
	}
};

/**
 * 対象点群にフォーカスする
 * @param {*} target PointCloud or Design
 */
export const zoomToTileset = (target) => {
	if (target instanceof PointCloud) {
		store.getters.viewer.zoomToTileset(target.getAssetId(), "pointcloud");
	} else if (target instanceof Design) {
		store.getters.viewer.zoomToTileset(target.getAssetId(), "design");
	} else if (target instanceof Line) {
		if (target.getLineType() === "dxf") {
			store.getters.viewer.zoomToDXF(target.getId());
		} else if (target.getLineType() === "sima") {
			store.getters.viewer.zoomToSIMA(target.getId());
		}
	}
	// 表示点群リストの先頭に移動
	// store.dispatch("addToVisiblePointCloudAndDesignList", target);
};

export const zoomToDrawing2dImage = (target) => {
	store.getters.viewer.zoomToDrawing2dImage(target.getAssetId());
};

export const removeDrawing2dImage = (drawing2dImage) => {
	store.getters.viewer.removeDrawing2dImage(drawing2dImage.assetId);
};

/**
 * 設計データの可視不可視を変更する
 * @param {*} design
 */
export const toggleDesignVisibility = async (design) => {
	if (!design.getVisibility()) {
		// 不可視から可視への切り替え
		if (design.getIsTiling()) {
			// タイリング済みの場合、新たにタイリング処理を行わない
			design.setVisibility(true);
			store.getters.viewer.updateTilesetVisibility(
				Number.parseInt(design.getAssetId()),
				"design",
				design.getVisibility(),
			);
			// 表示した設計データにフォーカスする
			zoomToTileset(design);
			await localCacheDB.addDesign(
				store.state.user.id,
				store.state.site.site_uuid,
				design.getAssetId(),
			);
		} else {
			// スピナーを表示する
			store.dispatch("updateIsLoading", true);
			// タイリング中のステータスをtrueにする
			design.setTilingProgress(true);
			try {
				// タイリングのためのデータを取得し、タイリング処理を実行する
				await addDesignDataTileset(design);
				// ステータスを可視状態かつタイリング済みにする
				design.setVisibility(true);
				design.setIsTiling(true);
				await localCacheDB.addDesign(
					store.state.user.id,
					store.state.site.site_uuid,
					design.getAssetId(),
				);

				// 表示点群リストに追加
				// store.dispatch("addToVisiblePointCloudAndDesignList", design);
			} catch (e) {
				const msg = e?.message ?? String(e);
				console.error(msg);
				await localCacheDB.deleteDesign(
					store.state.user.id,
					store.state.site.site_uuid,
					design.getAssetId(),
				);

				if (msg === "conversion error") {
					notifyError(
						{ $store: store, $t: i18n.global.t },
						`座標系が異なるため、タイリング中で計算で失敗`,
					);
				} else {
					notifyError(
						{ $store: store, $t: i18n.global.t },
						`タイリングに失敗しました`,
					);
				}
				throw e;
			} finally {
				// タイリング中のステータスをfalseに戻す
				design.setTilingProgress(false);
				// スピナーを非表示にする
				store.dispatch("updateIsLoading", false);
			}
		}
	} else {
		// 可視から不可視への切り替え
		design.setVisibility(false);
		await localCacheDB.deleteDesign(
			store.state.user.id,
			store.state.site.site_uuid,
			design.getAssetId(),
		);

		store.getters.viewer.updateTilesetVisibility(
			Number.parseInt(design.getAssetId()),
			"design",
			design.getVisibility(),
		);
		// 表示点群リストから削除
		// store.dispatch("removeFromVisiblePointCloudAndDesignList", design);
	}
};

export const toggleDrawing2dImageVisibility = async (drawing2dImage) => {
	if (!drawing2dImage.getIsVisible()) {
		await store.dispatch("executeWithSpinner", async () => {
			if (drawing2dImage.getIsLoaded()) {
				// 不可視から可視への切り替え
				drawing2dImage.setIsVisible(true);
				store.getters.viewer.updateDrawing2dImageVisibility(
					drawing2dImage.assetId,
					true,
				);
				// 表示した設計データにフォーカスする
				zoomToDrawing2dImage(drawing2dImage);
				await localCacheDB.addDrawing2DImage(
					store.state.user.id,
					store.state.site.site_uuid,
					drawing2dImage.getAssetId(),
				);
				// store.dispatch("addToVisiblePointCloudAndDesignList", drawing2dImage);
			} else {
				try {
					const [image, json] = await Promise.allSettled([
						axios.get(
							`${import.meta.env.VITE_API_BASE}/drawing2dImage/image/${drawing2dImage.assetId}`,
							{
								auth: store.state.authObject,
								responseType: "arraybuffer",
							},
						),
						axios.get(
							`${import.meta.env.VITE_API_BASE}/drawing2dImage/json/${drawing2dImage.assetId}`,
							{
								auth: store.state.authObject,
							},
						),
					]);
					if (json.status === "rejected") {
						throw json.reason;
					}
					if (image.status === "fulfilled") {
						drawing2dImage.setHasImage(true);
					}
					const blob =
						image.status === "fulfilled" ? new Blob([image.value.data]) : "";

					await store.getters.viewer.addDrawing2dImageFromJSON(
						drawing2dImage.assetId,
						blob,
						json.value.data,
					);
					drawing2dImage.setIsVisible(true);
					drawing2dImage.setTransparency(json.value.data.transparency ?? 0.5);
					const elevation = store.getters.viewer.getElevationOfDrawing2dImage(
						drawing2dImage.assetId,
					);
					if (Number.isFinite(elevation)) {
						drawing2dImage.setElevation(Number.parseFloat(elevation));
					}

					drawing2dImage.setIsLoaded(true);
					await localCacheDB.addDrawing2DImage(
						store.state.user.id,
						store.state.site.site_uuid,
						drawing2dImage.getAssetId(),
					);
				} catch (e) {
					console.error(e);
					store.getters.viewer.removeDrawing2dImage(drawing2dImage.assetId); // Just in case the drawing2dImage is rendered in the viewer
					drawing2dImage.setIsLoaded(false);
					await localCacheDB.deleteDrawing2DImage(
						store.state.user.id,
						store.state.site.site_uuid,
						drawing2dImage.getAssetId(),
					);
				}
			}
		});
	} else {
		// 可視から不可視への切り替え
		drawing2dImage.setIsVisible(false);
		store.getters.viewer.updateDrawing2dImageVisibility(
			drawing2dImage.assetId,
			false,
		);
		await localCacheDB.deleteDrawing2DImage(
			store.state.user.id,
			store.state.site.site_uuid,
			drawing2dImage.getAssetId(),
		);
	}
};

export const updateDrawing2dImageTransparency = async (
	drawing2dImage,
	transparency,
) => {
	store.getters.viewer.updateDrawing2dImageTransparency(
		Number.parseInt(drawing2dImage.assetId),
		transparency,
	);
};

export const addDrawing2dImage = async (drawing2dImage) => {
	const convertToBaseInfo = (image) => {
		return {
			file: image.file,
			name: image.name,
			assetId: image.assetId,
			centerLatitude: Number(image.latitude),
			centerLongitude: Number(image.longitude),
			rotateDegree: image.rotateDegree,
			imageWidth: image.imageWidth,
			imageHeight: image.imageHeight,
			isDraped: image.isDraped,
			isVisible: image.isVisible,
			transparency: image.transparency,
		};
	};

	if (Array.isArray(drawing2dImage)) {
		const promises = drawing2dImage.map(async (image) => {
			const drawing2dImageBaseInfo = convertToBaseInfo(image);
			return store.getters.viewer.addDrawing2dImage(drawing2dImageBaseInfo);
		});
		await Promise.all(promises);
	} else {
		const drawing2dImageBaseInfo = convertToBaseInfo(drawing2dImage);
		await store.getters.viewer.addDrawing2dImage(drawing2dImageBaseInfo);
	}
};

export const hexToRgb = (hex) => {
	let c;
	if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
		c = hex.substring(1).split("");
		if (c.length == 3) {
			c = [c[0], c[0], c[1], c[1], c[2], c[2]];
		}
		c = "0x" + c.join("");
		return { r: (c >> 16) & 255, g: (c >> 8) & 255, b: c & 255, a: 1 };
	}
	throw new Error("Bad Hex");
};

export const convertCoordinateToCesiumCrs = async ({ x, y, z }) => {
	try {
		const result = await convertCoordinatesToCesiumCrs([{ x, y, z }]);
		return result.length > 0
			? result[0]
			: { x: Number.NaN, y: Number.NaN, z: Number.NaN };
	} catch (e) {
		return { x: Number.NaN, y: Number.NaN, z: Number.NaN };
	}
};

//hack to make worker synchronous
const mapOfPromises = new Map();
worker.onmessage = (e) => {
	const result = JSON.parse(e.data);
	const id = result.id;
	const resolve = mapOfPromises.get(id);
	resolve?.(result.wgs84Coords);
	mapOfPromises.delete(id);
};

export const convertCoordinatesToCesiumCrs = async (coords) => {
	console.log("Sending Local Coordinates of length", coords.length);
	const data = {
		action: "TRANSFORM",
		payload: { coords, site: store.state.site },
	};
	return new Promise<{ x: number; y: number; z: number }[]>((resolve) => {
		const id = uuidv4();
		worker.postMessage(JSON.stringify({ id, data }));
		mapOfPromises.set(id, resolve);
	});
};

export const convertCoordinateToPointCloudCrs = async ({ x, y, z }) => {
	try {
		const result = await convertPointsToPointCloudCRS([{ x, y, z }]);
		return result.length > 0
			? result[0]
			: { x: Number.NaN, y: Number.NaN, z: Number.NaN };
	} catch (e) {
		return { x: Number.NaN, y: Number.NaN, z: Number.NaN };
	}
};

export const convertCoordinatesToPointCloudCrs = async (coords) => {
	let result = [];
	try {
		result = (await convertPointsToPointCloudCRS(coords)) || [];
	} catch (e) {
		result = [];
	}
	return result;
};

export const addPointCloudTileset = async (point_cloud) => {
	const heightmapResponse = await axios.get(
		`${import.meta.env.VITE_API_BASE}/pointcloud/heightmap/${point_cloud.point_cloud_id}`,
		{
			auth: store.state.authObject,
			withCredentials: true,
		},
	);
	let geometryBuffer;
	if (!heightmapResponse?.data?.hasData) {
		console.log("Height Map not found");
		console.log("Start to fetch Geometry");
		const geometryResponse = await axios.get(
			`${import.meta.env.VITE_API_BASE}/pointcloud/data/${point_cloud.point_cloud_id}`,
			{
				auth: store.state.authObject,
				withCredentials: true,
			},
		);
		console.log("Geometry Fetched");

		//geometry response is in wgs84
		if (geometryResponse?.data && geometryResponse?.data.length > 0) {
			console.log("Converting points to the Site coordinate", {
				x: geometryResponse.data[0],
				y: geometryResponse.data[1],
				z: geometryResponse.data[2],
			});

			const localPositions = [];
			// Rather than converting points one at a time or all at once, it is better to process in batch
			// Here batch of size 1000 points has been choosen
			const batchLength = 1000;

			// geometryResponse.data.length is a buffer/array of numbers organized in [x,y,z,x,y,z ....] fashion.
			// batchlength is multiplied by 3 to make a batch of 1000 points (point = {x,y,z})
			for (let i = 0; i < geometryResponse.data.length; i += batchLength * 3) {
				const endindex =
					geometryResponse.data.length - i >= batchLength * 3
						? i + batchLength * 3
						: geometryResponse.data.length;
				const wgs84positions = [];
				for (let j = i; j < endindex; j += 3) {
					wgs84positions.push({
						x: geometryResponse.data[j],
						y: geometryResponse.data[j + 1],
						z: geometryResponse.data[j + 2],
					});
				}
				const positions =
					await convertCoordinatesToPointCloudCrs(wgs84positions);
				localPositions.push(...positions);
				console.log(
					`Converting points to the Site coordinate: ${(endindex / geometryResponse.data.length) * 100}% completed`,
				);
			}
			if (localPositions.length === 0) throw new Error("geometry is empty");
			geometryBuffer = localPositions.flatMap((p) => [p.x, p.y, p.z]);
		}
	}
	if (!heightmapResponse?.data?.hasData && !geometryBuffer) {
		throw new Error(
			`Point cloud ${point_cloud.point_cloud_id} has neither heightmap nor triangulated geometry. Please re-upload the point cloud.`,
		);
	}
	return store.getters.viewer.addTileset(
		Number.parseInt(point_cloud.asset_id),
		"pointcloud",
		{
			heightMapInput: heightmapResponse.data.hasData
				? heightmapResponse.data.height_map
				: undefined,
			vertexBuffer: geometryBuffer,
			styleOptions: {
				common: { transparency: point_cloud.getTransparency() },
			},
		},
		point_cloud.asset_id >= Number(import.meta.env.VITE_TILE_ASSET_ID)
			? {
					accessToken: getCookie("access_token"),
					version: point_cloud.getVersion(),
					tileAssetUrl: import.meta.env.VITE_API_TILE_ASSET,
				}
			: null,
	);
};

export const addDesignDataTileset = async (design) => {
	const response = await axios.get(
		`${import.meta.env.VITE_API_BASE}/design/data/${design.design_id}`,
		{
			auth: store.state.authObject,
			withCredentials: true,
		},
	);
	const wgs84Points = [];
	for (let i = 0; i < response.data.vertices.length; i += 3) {
		wgs84Points.push({
			x: response.data.vertices[i],
			y: response.data.vertices[i + 1],
			z: response.data.vertices[i + 2],
		});
	}
	const localPoints = await convertCoordinatesToPointCloudCrs(wgs84Points);
	response.data.vertices = localPoints.flatMap((w) => [w.x, w.y, w.z]);
	return store.getters.viewer.addTileset(
		Number.parseInt(design.asset_id),
		"design",
		{
			vertexBuffer: response.data.vertices,
			indexBuffer: response.data.indices,
			styleOptions: {
				design: { mesh_color: hexToRgb(design.getMeshColor()) },
				common: { transparency: design.getTransparency() },
			},
		},
		design.asset_id >= Number(import.meta.env.VITE_TILE_ASSET_ID)
			? {
					accessToken: getCookie("access_token"),
					version: design.getVersion(),
					tileAssetUrl: import.meta.env.VITE_API_TILE_ASSET,
				}
			: null,
	);
};

export const setAssociatedTerrainVisible = async (terrain) => {
	if (terrain.getVisibility()) {
		zoomToTileset(terrain);
		return;
	}
	switch (terrain.getType()) {
		case TerrainType.POINT_CLOUD:
			await togglePointCloudVisibility(terrain);
			break;
		case TerrainType.DESIGN_DATA:
			await toggleDesignVisibility(terrain);
			break;
		case TerrainType.REFERENCE_TOPOLOGY:
			await toggleDrawing2dImageVisibility(terrain);
			break;
		default:
			throw new Error(`Unknown terrain type: ${terrain.getType()}`);
	}
};

/**
 * 線形の表示ステータスを切り替える
 * @param {*} entity
 */
export const toggleLinearVisibility = async (entity) => {
	const type = "linear";
	if (!entity.isLinearVisible) {
		if (entity.isLinearLoaded) {
			// 読み込み済みの場合は表示ステータスのみ切り替え
			await store.getters.viewer.showMeasurement(
				String(entity.measurementId),
				type,
			);
			entity.isLinearVisible = true;
		} else {
			// 未読み込みの場合はviewerに読み込む
			await store.getters.viewer.loadMeasurement(
				String(entity.measurementId),
				type,
				{
					start: entity.startPoints,
					end: entity.endPoints,
				},
			);
			await store.getters.viewer.showMeasurement(
				String(entity.measurementId),
				type,
			);
			entity.isLinearVisible = true;
			entity.isLinearLoaded = true;
		}
	} else {
		store.getters.viewer.hideMeasurement(String(entity.measurementId), type);
		entity.isLinearVisible = false;
	}
	return;
};

/**
 * 距離の表示ステータスを切り替える
 * @param {*} entity
 */
export const toggleDistanceVisibility = async (entity) => {
	const type = "distance";
	if (!entity.isDistanceVisible) {
		if (entity.isDistanceLoaded) {
			// 読み込み済みの場合は表示ステータスのみ切り替え
			await store.getters.viewer.showMeasurement(
				String(entity.measurementId),
				type,
			);
			entity.isDistanceVisible = true;
		} else {
			// 未読み込みの場合はviewerに読み込む
			await store.getters.viewer.loadMeasurement(
				String(entity.measurementId),
				type,
				{
					start: entity.startPoints,
					end: entity.endPoints,
				},
			);
			await store.getters.viewer.showMeasurement(
				String(entity.measurementId),
				type,
			);
			entity.isDistanceVisible = true;
			entity.isDistanceLoaded = true;
		}
	} else {
		store.getters.viewer.hideMeasurement(String(entity.measurementId), type);
		entity.isDistanceVisible = false;
	}
	return;
};

/**
 * 断面の表示ステータスを切り替える
 * @param {*} entity
 */
export const toggleCrossSectionVisibility = async (entity) => {
	const type = "crossSection";
	if (!entity.isCrossSectionVisible) {
		if (entity.isCrossSectionLoaded) {
			// 読み込み済みの場合は表示ステータスのみ切り替え
			await showMeasurementCrossSection(entity);
			entity.isCrossSectionVisible = true;
		} else {
			// 未読み込みの場合はviewerに読み込む
			await store.getters.viewer.loadMeasurement(
				String(entity.measurementId),
				type,
				// assetId,
				{
					start: entity.startPoints,
					end: entity.endPoints,
				},
			);
			await showMeasurementCrossSection(entity);
			entity.isCrossSectionVisible = true;
			entity.isCrossSectionLoaded = true;
		}
	} else {
		hideMeasurementCrossSection(entity);
		entity.isCrossSectionVisible = false;
	}
	return;
};

/**
 * 2点と紐づく線形、距離、断面をviewer上から削除する
 * @param {*} entity
 */
export const deleteMeasurement = (entity) => {
	const types = ["linear", "distance", "crossSection"];
	for (const type of types) {
		// 非表示にする
		if (type === "crossSection") {
			hideMeasurementCrossSection(entity);
		} else {
			store.getters.viewer.hideMeasurement(String(entity.measurementId), type);
		}
	}
	// 削除実行
	store.getters.viewer.deleteMeasurement(String(entity.measurementId));
	entity.reset();
};

export const showMeasurementCrossSection = async (measurement) => {
	const type = "crossSection";
	await store.getters.viewer.showMeasurement(
		String(measurement.measurementId),
		type,
	);
	store.dispatch("measurement/setActiveArbitraryCrossSection", measurement);
};

export const hideMeasurementCrossSection = async (measurement) => {
	const type = "crossSection";
	store.getters.viewer.hideMeasurement(String(measurement.measurementId), type);
	store.dispatch("measurement/setActiveArbitraryCrossSection", null);
};

export const getCornerPositions = (
	latitude,
	longitude,
	imageWidth,
	imageHeight,
	rotateDegree,
) => {
	const cornerPositions = store.getters.viewer.getCornerPositions(
		Number(latitude),
		Number(longitude),
		Number(imageWidth),
		Number(imageHeight),
		Number(rotateDegree),
	);
	return cornerPositions;
};

export const pickPositions = async (numberOfPositionToPick) => {
	const positions = store.getters.viewer.pickPointsForRoadConstruction_AI(
		numberOfPositionToPick,
	);
	return positions;
};

/**
 * Flies the Cesium camera to a specified longitude, latitude, and height.
 * Uses a helper method `window.viewer.flyToLongLatHeight` which wraps Cesium's camera.flyTo.
 *
 * @param {Object} params - The parameters for the camera flight.
 * @param {number} params.longitude - The longitude in degrees.
 * @param {number} params.latitude - The latitude in degrees.
 * @param {number} params.height - The height in meters above the ellipsoid.
 * @param {number} [params.duration=0] - The duration of the flight in seconds. Use 0 for instant movement.
 */
export const flyToLongLatHeight = ({
	longitude,
	latitude,
	height,
	duration,
}) => {
	store.getters.viewer.flyToLongLatHeight({
		longitude,
		latitude,
		height,
		duration,
	});
};

export const set3DView = () => {
	store.getters.viewer.set3DView();
};

export const setTopViewCameraToObject = (objectId) => {
	store.getters.viewer.setTopViewCameraToObject(objectId);
};

export const set2DViewFromCurrentPosition = () => {
	store.getters.viewer.set2DViewFromCurrentPosition();
};

/**
 * Duplicate an object
 * @param {Object} originalObj - The source object to duplicate
 * @param {string} siteId - Site ID
 * @returns {Promise<Object>} Information of the duplicated object
 */
export const duplicateObject = async (object: IObject, siteId) => {
	try {
		if (!object.toDuplicateJSON) {
			throw new Error("Object does not support duplication");
		}
		const duplicatedObj = await object.toDuplicateJSON();

		if (!duplicatedObj.id || !duplicatedObj.cid || !duplicatedObj.name) {
			console.error("Object missing required properties:", duplicatedObj);
			throw new Error("Object missing required properties");
		}

		const response = await axios.post(
			`${import.meta.env.VITE_API_BASE}/obj/${siteId}`,
			{
				siteId: siteId,
				objJSON: "",
				objProps: duplicatedObj,
				objConnectionJSON: null,
			},
			{
				auth: store.state.authObject,
			},
		);
		return { json_name: response.data.json_name };
	} catch (error) {
		console.error("Duplication failed:", error);
		throw error;
	}
};
